import { Component, createRef } from 'react';
import styles from './editor.module.scss';
import bulkStyles from './bulk-landing.module.scss';
import { EditorComponent } from './editor.component';
import { detectFormat } from './codecs/detect';
import { readHead } from './codecs/canvas-utils';
import { landingFor, emptyStateCopy, LandingSpec, EMPTY_FORMATS_LINE } from './routing';
import { isBulkOn, acceptedExtensions, acceptAttr, pasteAccepts, landingCopy, skippedMessage, duplicateMessage, sameFile, LEAVE_WARNING, blockedAtIntake, converterFor, SkipLink } from './bulk-gate';
import { BATCH_LIMIT } from './model/batch';
import { pickUpTransferredFiles, HandoffMissReason } from '../../utils/pickup-transferred-files';
import { transferTray } from '../../services/transfer-tray.service';
import FileUploadComponent from '../converter/file-upload/file-upload.component';
import { SessionFilesComponent } from '../converter/session-files/session-files.component';
import { FeatureDetailsComponent } from '../ui/feature-details/feature-details.component';
import { RelatedLinksComponent } from '../ui/related-links/related-links.component';
import RatingDisplayComponent from '../ui/rating-display/rating-display.component';
import { MotiveComponent } from '../ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import { MotiveLocationTypes } from '../../utils/request.utils';
import { MetricsService } from '../../services/metrics.service';
import { SettingsService } from '../../services/settings.service';
import { container } from '../../context/module';
import type { Subscription } from 'rxjs';
import { FileSelectEvent } from '../../types/file-upload.types';
import { PageData, RouteProps } from '../../types/route-types';
import { RouteUtils } from '../../utils/route.utils';

interface RootState {
    files: File[];
    dragOver: boolean;
    rejected: string | null;
    /** The converter that opens a skipped file (J12-1, J12-8, J8-11), shown as a link under `rejected`. */
    rejectedLink: SkipLink | null;
    /** featureFlags.imageEditorBulk (bulk-gate.ts). False until the flag emits — flag off = today. */
    bulk: boolean;
    /** Touch-only device (no drag and drop): the drop wording is left out (phone-13). Set after mount. */
    touch: boolean;
    /** Apple keyboard (⌘V, not Ctrl+V) in the paste hint (IMAGEEDITOR-40). Set after mount. */
    mac: boolean;
}

/**
 * Route entry for /image-resizer, /bulk-image-resizer (batch-first when featureFlags.imageEditorBulk
 * is on — bulk-gate.ts) and every resize-* / crop-* / rotate-* / flip-* slug
 * (route-to-component-map.ts). Owns the file list; hands it to EditorComponent once there is one.
 * The empty state is the first thing a visitor sees, so it names the page's tool and format
 * (routing.ts) and offers files handed over from other tools and recent files.
 */

export default class EditorRootComponent extends Component<RouteProps, RootState> {
    private landing: LandingSpec;
    private title = '';
    private description = '';
    private feature = '';
    private fileInput = createRef<HTMLInputElement>();
    private editorH1 = createRef<HTMLHeadingElement>();
    private seoRef = createRef<HTMLDivElement>();
    private flagSub: Subscription | null = null;

    constructor(props: RouteProps) {
        super(props);
        this.landing = landingFor(props.path);
        this.feature = RouteUtils.getPathFromProps(props);
        const pd: PageData | undefined = props.pageData;
        this.title = pd?.page_seo?.title || '';
        this.description = (pd?.page_seo?.description || '').replace(/\s+/g, ' ');
        this.state = { files: [], dragOver: false, rejected: null, rejectedLink: null, bulk: false, touch: false, mac: false };
    }

    /** Extensions this page opens right now: today's list, plus EPS/RAW when the bulk flag is on. */
    private get accepted(): string[] { return acceptedExtensions(this.state.bulk); }

    componentDidMount() {
        // IMG-QA1-robust-1: fetch the decoders' code while the page idles, before any file is picked, so a photo
        // chosen after the connection drops still opens in the browser (the editor retries the chunk itself too)
        if (typeof window !== 'undefined') {
            const w = window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
            const prefetch = () => { EditorComponent.loaders.codecs().catch(() => undefined); };
            if (w.requestIdleCallback) w.requestIdleCallback(prefetch, { timeout: 5000 }); else this.prefetchTimer = setTimeout(prefetch, 3000);
        }
        // Subscribe first: the settings ReplaySubject replays the SSR-props emission synchronously,
        // so the hand-off below already sees the widened list when the flag is on.
        let bulkAtMount = false;
        try {
            this.flagSub = container.get(SettingsService).featureFlags.subscribe(flags => {
                bulkAtMount = isBulkOn(flags);
                if (bulkAtMount !== this.state.bulk) this.setState({ bulk: bulkAtMount });
            });
        } catch { /* no settings = flag off */ }
        // J5-8 / J10-8 / J13-8: this history entry is the Back guard of an editor that had photos open, so
        // the page was reloaded or came back from another page and the photos are gone. Say so, point at
        // "Use a recent file" (stage() remembers what was opened), and let Back skip the stale same-URL entry.
        let onGuardEntry = false;
        try { onGuardEntry = !!window.history.state?.xcEditorGuard; } catch { /* sandboxed */ }
        this.onStaleGuard = onGuardEntry;
        if (onGuardEntry && !new URLSearchParams(window.location.search).get('xfer')) {
            // IMAGEEDITOR-R2 i-6b: a file handed over from another page (?xfer, stripped on arrival) is opened again
            // from this device's recent files; anything else says what was lost, as before
            this.setState({ rejected: LOST_ON_RELOAD, rejectedLink: null });
            void this.restageHandoff();
        }
        pickUpTransferredFiles({ acceptedExtensions: acceptedExtensions(bulkAtMount), pageSlug: this.landing.slug, stage: files => this.stage(files, false, true), onMissed: this.onHandoffMissed });
        document.addEventListener('paste', this.onPaste);
        // Drop a photo anywhere on the page while the editor is open to add it (UX review N1b); the
        // empty-state zone handles its own drop first and marks the event handled.
        document.addEventListener('dragover', this.onDocDrag);
        document.addEventListener('drop', this.onDocDrop);
        // Reload / close with photos loaded: the browser asks first (regression-5 a); Back is guarded in
        // componentDidUpdate with a same-URL history entry (regression-5 b).
        window.addEventListener('beforeunload', this.onBeforeUnload);
        window.addEventListener('popstate', this.onPopState);
        try { if (window.matchMedia?.('(hover: none) and (pointer: coarse)').matches) this.setState({ touch: true }); } catch { /* no matchMedia */ }
        try { if (/Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '')) this.setState({ mac: true }); } catch { /* no navigator */ }
        this.outlineStatics();
    }
    /** a11y-12 (final sweep): the shared static blocks keep their h4 / h5 tags (their styles, the card split
     *  and the HowTo/FAQ parsers rely on them - feedback_markdown_heading_levels), but on this page they sit
     *  right under the H1: markdown sections are level 2 and its questions level 3; the related-links title is
     *  a section, level 2. The rating title is level 3 (last mile 2): it is the page's last heading and the site
     *  footer's native h4 columns follow it, so a level-2 there is a 2→4 skip for axe heading-order; the footer
     *  is shared and stays as it is, so the step is taken on this side. No skipped level in both flag states. */
    private outlineStatics() {
        this.seoRef.current?.querySelectorAll('h4, h5, h6').forEach(h => {
            const level = h.closest('[data-outline="md"]') ? Number(h.tagName.slice(1)) - 2 : h.closest('[data-outline="rating"]') ? 3 : 2;
            if (h.getAttribute('aria-level') !== String(level)) h.setAttribute('aria-level', String(level));
        });
    }
    componentDidUpdate(_: RouteProps, prev: RootState) {
        this.outlineStatics();
        if (!prev.files.length && this.state.files.length) {
            this.armHistoryGuard();
            // a11y-3 (e): the upload button that had focus just unmounted, which drops focus to BODY.
            // Move it to the editor's heading so keyboard / screen-reader users land in the editor;
            // never steal focus from a control the user is already on.
            const a = typeof document !== 'undefined' ? document.activeElement : null;
            if (!a || a === document.body || !a.isConnected) this.editorH1.current?.focus();
        }
    }
    /** J5-2: a hand-off was on the URL but nothing arrived; say it where the user looks, not only in telemetry. */
    private onHandoffMissed = (reason: HandoffMissReason, names: string[]) => {
        if (this.state.files.length) return;
        const which = names.length === 1 ? names[0] : 'The file';
        this.setState({
            rejected: reason === 'expired'
                ? 'The file from the last page did not come across (it may have expired). Choose it again below.'
                : `${which} from the last page is not a file this editor opens. Choose another file below.`,
            rejectedLink: names.length ? converterFor(names[0], this.state.bulk) : null,
        });
    };
    /** IMAGEEDITOR-R2 i-6b: reload after a hand-off. The ?xfer id is gone (stripped on arrival, the tray record taken),
     *  but stage() remembered the files on this device; the names handed over are kept for the tab (sessionStorage), so the
     *  same files open again instead of an empty uploader. Nothing found = the LOST_ON_RELOAD line stays. */
    private async restageHandoff() {
        let kept: { slug: string; files: { name: string; size: number }[]; open?: number } | null = null;
        try { kept = JSON.parse(window.sessionStorage.getItem(HANDOFF_KEY) || 'null'); } catch { kept = null; }
        if (!kept || kept.slug !== this.landing.slug || !kept.files?.length) return;
        let recs: { file: File; name: string; size: number }[] = [];
        try { recs = await transferTray.session(); } catch { return; }
        const files = kept.files.map(k => recs.find(r => r.name === k.name && r.size === k.size)?.file).filter((f): f is File => !!f);
        if (!files.length || this.state.files.length) return;
        const back = (await this.stage(files, false, true)).length;   // this.state may not have caught up after the await
        if (!back) return;
        // fewer back than were open (6+ photos, or one no longer in recent files): say so instead of a silent gap
        const open = Math.max(kept.open ?? 0, kept.files.length);
        this.setState(back < open ? { rejected: partlyReopened(back, open), rejectedLink: null } : { rejected: null, rejectedLink: null });
    }
    /** i-6b: while a hand-off's set is remembered for this tab, it follows "Add more" and removals, so a reload re-opens
     *  what was open (recent files keep up to 6; `open` counts all of them for the notice above). */
    private keepHandoff(files: File[]) {
        try {
            const kept = JSON.parse(window.sessionStorage.getItem(HANDOFF_KEY) || 'null');
            if (!kept || kept.slug !== this.landing.slug) return;
            if (!files.length) { window.sessionStorage.removeItem(HANDOFF_KEY); return; }
            window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ slug: this.landing.slug, files: files.slice(0, 6).map(f => ({ name: f.name, size: f.size })), open: files.length }));
        } catch { /* no sessionStorage */ }
    }
    // ---- leave guards (regression-5): the files and edits live only in this page
    private guardArmed = false;
    /** Mounted on a guard entry left by an earlier visit (reload / Back from another page). */
    private onStaleGuard = false;
    /** The user already said yes to leaving: the browser's own prompt must not ask again (J2-11, J8-9). */
    private leaving = false;
    private onBeforeUnload = (e: BeforeUnloadEvent) => {
        if (!this.state.files.length || this.leaving) return;
        e.preventDefault(); e.returnValue = LEAVE_WARNING; return LEAVE_WARNING;
    };
    /** One extra same-URL entry while photos are open: Back pops it and we ask before leaving. The entry
     *  copies the router's own state so Next.js treats a pop onto it as this same page. */
    private armHistoryGuard() {
        if (this.guardArmed || typeof window === 'undefined' || !window.history?.pushState) return;
        // Already standing on a guard entry (after a reload): reuse it rather than stacking a second one.
        if (this.onStaleGuard) { this.onStaleGuard = false; this.guardArmed = true; return; }
        try { window.history.pushState({ ...(window.history.state || {}), xcEditorGuard: 1 }, '', window.location.href); this.guardArmed = true; } catch { /* sandboxed */ }
    }
    private onPopState = (e: PopStateEvent) => {
        // J5-8: Back from the stale guard entry lands on the same URL with nothing open: keep going back,
        // so one Back reaches the previous page (e.g. /downloads) as it would on a fresh visit.
        if (this.onStaleGuard && !this.state.files.length && !(e.state && e.state.xcEditorGuard)) {
            this.onStaleGuard = false;
            try { window.history.back(); } catch { /* sandboxed */ }
            return;
        }
        if (!this.guardArmed || (e.state && e.state.xcEditorGuard)) return;
        this.guardArmed = false;
        if (!this.state.files.length) return;   // nothing to lose: Back already happened
        let leave = false;
        try { leave = window.confirm(LEAVE_WARNING); } catch { leave = false; }
        if (leave) { this.leaving = true; window.history.back(); }
        else this.armHistoryGuard();
    };
    private prefetchTimer: ReturnType<typeof setTimeout> | null = null;
    componentWillUnmount() {
        if (this.prefetchTimer) clearTimeout(this.prefetchTimer);
        this.flagSub?.unsubscribe();
        document.removeEventListener('paste', this.onPaste);
        document.removeEventListener('dragover', this.onDocDrag);
        document.removeEventListener('drop', this.onDocDrop);
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        window.removeEventListener('popstate', this.onPopState);
    }
    private onDocDrag = (e: DragEvent) => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault(); };
    private onDocDrop = (e: DragEvent) => {
        if (e.defaultPrevented) return;
        const files = Array.from(e.dataTransfer?.files || []); if (!files.length) return;
        e.preventDefault();
        this.stage(files, this.state.files.length > 0);
    };
    /** Ctrl/⌘+V an image (a screenshot, a copied photo) opens it — the desktop power path (UX review N1). */
    private onPaste = (e: ClipboardEvent) => {
        const t = e.target as HTMLElement | null; if (t && /^(INPUT|TEXTAREA)$/.test(t.tagName)) return;
        const files = Array.from(e.clipboardData?.files || []).filter(f => pasteAccepts(f, this.state.bulk));
        if (!files.length) return;
        e.preventDefault();
        this.stage(files, this.state.files.length > 0);
        this.telemetry('EDITOR_PICKER_OPENED', { tool: this.landing.tool, via: 'paste' });
    };

    private telemetry = (event: string, attrs?: Record<string, unknown>) => {
        try { container.get(MetricsService).emit(event as any, { page: this.landing.slug, ...(attrs || {}) }); } catch { /* telemetry never blocks */ }
    };

    /** The single path every file takes in: picker, drop, hand-off, recent. Filters by extension, caps the batch. */
    private async stage(incoming: File[], append = false, handoff = false): Promise<File[]> {
        const ext = (f: File) => (f.name.split('.').pop() || '').toLowerCase();
        // Accept by extension OR MIME type: Android's photo picker hands files a content-provider
        // name like "image:1000047842" with no extension but a correct type ("image/jpeg"), which
        // extension-only filtering wrongly rejected (QA on a phone, 2026-09-06). Any image/* type,
        // plus the HEIC/HEIF/PSD types browsers spell inconsistently, is allowed in; the codec
        // sniffs the real format from the bytes and fails gracefully if it truly can't open it.
        const okType = (f: File) => /^image\//i.test(f.type) || /(heif|heic|photoshop|x-icon|vnd\.microsoft\.icon)/i.test(f.type);
        const accepted0 = this.accepted;
        const takes = (f: File) => accepted0.includes(ext(f)) || okType(f);
        // Third case (owner's phone, 2026-09-06): the Files app's "Images" category hands over
        // "image%3A1000047842" with NO extension and an EMPTY type for a plain JPEG. Sniff the first
        // 32 bytes; if it is a format we open, wrap it in a File with a real name + type so the codec
        // dispatch and the download name both work ("photo-1000047842.jpg").
        const resolved = await Promise.all(incoming.map(async f => {
            if (takes(f)) return f;
            try {
                // recheck 2026-10-08 P1 J16-1: content AND name, as the decoder does - camera.ARW (an ARW is a TIFF
                // header) sniffed as 'tiff' alone was renamed camera.tiff and failed in the TIFF reader; the
                // extension makes a TIFF-shaped RAW 'raw', so it is skipped with the RAW converter link instead
                const fmt = detectFormat(f, await readHead(f));
                if (fmt !== 'unknown' && fmt !== 'raw' && accepted0.includes(fmt)) {
                    const base = (decodeURIComponent(f.name).replace(/^image:/i, 'photo-').replace(/[^\w.-]+/g, '-').replace(/\.[^.]*$/, '') || 'photo');
                    return new File([f], `${base}.${fmt}`, { type: `image/${fmt === 'jpg' ? 'jpeg' : fmt}`, lastModified: f.lastModified });
                }
            } catch { /* unreadable: falls through to the skip message */ }
            return f;
        }));
        const bulk = this.state.bulk;
        const takes2 = (f: File) => takes(f) && !blockedAtIntake(f, bulk);
        const taken = resolved.filter(takes2);
        const skipped = resolved.filter(f => !takes2(f)).map(f => f.name);
        // critic-6: the same photo dropped / added again while it is open is not added twice
        const have = append ? this.state.files : [];
        const dupes = taken.filter((f, i) => have.some(h => sameFile(h, f)) || taken.slice(0, i).some(h => sameFile(h, f)));
        const accepted = taken.filter(f => dupes.indexOf(f) < 0);
        const files = (append ? [...this.state.files, ...accepted] : accepted).slice(0, BATCH_LIMIT);
        const over = (append ? this.state.files.length : 0) + accepted.length - files.length;
        const leftOut = accepted.slice(accepted.length - Math.max(0, over)).map(f => f.name);
        // every reason a chosen file is not in the batch, shown in the editor too (power-3, critic-6)
        const parts = [
            skipped.length ? skippedMessage(skipped.length === 1 ? skipped[0] : skipped.length + ' files', bulk, skipped) : '',
            dupes.length ? duplicateMessage(dupes.map(f => f.name)) : '',
            over > 0 ? capMessage(leftOut, append && this.state.files.length >= BATCH_LIMIT) : '',
        ].filter(Boolean);
        const rejected = parts.length ? parts.join(' ') : null;
        const rejectedLink = skipped.map(n => converterFor(n, bulk)).find(Boolean) || null;
        if (append && files.length === this.state.files.length && this.state.files.length) { this.setState({ rejected, rejectedLink, dragOver: false }); return this.state.files; }
        this.setState({ files, rejected, rejectedLink, dragOver: false });
        if (accepted.length) {
            this.telemetry('EDITOR_FILES_STAGED', { count: files.length, tool: this.landing.tool });
            // J10-8 / J13-8: a reload or Back drops what is open (the warning says so); the photos stay
            // offered under "Use a recent file" on this device (IndexedDB, 4 h, never uploaded).
            try { void transferTray.remember(files.slice(0, 6), this.landing.slug, this.title || this.landing.slug).catch(() => {}); } catch { /* convenience */ }
            // i-6b: what a hand-off opened is re-opened on a reload of this tab; any other new set replaces that
            try {
                if (handoff) window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ slug: this.landing.slug, files: files.slice(0, 6).map(f => ({ name: f.name, size: f.size })), open: files.length }));
                else if (!append) window.sessionStorage.removeItem(HANDOFF_KEY);
            } catch { /* no sessionStorage */ }
            if (append && !handoff) this.keepHandoff(files);
        }
        return files;
    }

    private onSelect = (ev: FileSelectEvent, append = false) => {
        const files = (ev.files || []).filter((f): f is File => typeof File !== 'undefined' && f instanceof File);
        if (files.length) this.stage(files, append);
    };

    private onDrop = (e: React.DragEvent) => { e.preventDefault(); const files = Array.from(e.dataTransfer?.files || []); if (files.length) this.stage(files, this.state.files.length > 0); };
    private onDrag = (over: boolean) => (e: React.DragEvent) => { e.preventDefault(); if (this.state.dragOver !== over) this.setState({ dragOver: over }); };

    /** "Add more" with a full batch says so instead of opening a picker whose choice is dropped (J3-12). */
    private openPicker = () => {
        if (this.state.files.length >= BATCH_LIMIT) { this.setState({ rejected: capMessage([], true), rejectedLink: null }); return; }
        this.fileInput.current?.click();
    };

    render() {
        const { files } = this.state;
        return <div className={styles.root}>
            {files.length === 0 ? this.emptyState() : <>
                {/* a11y-12 / firsttime-13: the page keeps one H1 once the editor opens */}
                <h1 ref={this.editorH1} tabIndex={-1} className={styles.srOnly} data-testid="editor-h1">{this.heading()}</h1>
                {/* No `key`: adding files never remounts the editor (power-2, critic-1) - it appends them,
                    keeping every edit, own crop, running download and upload. */}
                <EditorComponent files={files} landing={this.landing} bulk={this.state.bulk}
                    onChangeFiles={() => { try { window.sessionStorage.removeItem(HANDOFF_KEY); } catch { /* none */ } this.setState({ files: [], rejected: null, rejectedLink: null }); }} onAddMore={this.openPicker} onTelemetry={this.telemetry}
                    onFilesChange={(fs: File[]) => { this.keepHandoff(fs); this.setState({ files: fs }); }}
                    notice={this.state.rejected} onDismissNotice={() => this.setState({ rejected: null, rejectedLink: null })} />
            </>}
            {/* hidden multi-file input for "Add more" while the editor is open */}
            <input ref={this.fileInput} type="file" multiple accept={acceptAttr(this.state.bulk)} style={{ display: 'none' }}
                onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) this.stage(fs, true); e.target.value = ''; }} />
            {this.seoContent()}
        </div>;
    }

    private emptyState() {
        const copy = emptyStateCopy(this.landing);
        // FLAG-INVARIANT (QA round 2 redesign, 2026-10-05): H1, subtitle, drop zone, button and formats
        // line never read state.bulk. The flag lands ~1 s after first paint (SSG paints it undefined);
        // round 1 reserved space on /bulk-image-resizer only and the other 69 slugs still shifted
        // (qa2 NEW-1, R1-F3) and claimed "edited in your browser" for formats the flag sends to our
        // servers (N2). /bulk-image-resizer is batch-first by slug, not by flag. The flag still decides
        // intake (accept attribute, extension filter, server path) - nothing a visitor sees before
        // choosing a file. Supersedes PLAN's "flag off = H1 Image Resizer" for the bulk slug.
        const { heading, sub, batchFirst } = this.copy();
        return this.emptyMarkup(copy, heading, sub, batchFirst);
    }
    private copy() { return landingCopy(this.landing, !!this.landing.batchFirst, this.title, this.description); }
    private heading(): string { return this.copy().heading; }
    private emptyMarkup(copy: ReturnType<typeof emptyStateCopy>, heading: string, sub: string, batchFirst: boolean) {
        return <div className={styles.empty} data-testid="editor-empty">
            <div>
                <h1>{heading}</h1>
                {sub && <p className={styles.sub}>{sub}</p>}
            </div>
            <div className={`${styles.drop} ${this.state.dragOver ? styles.over : ''} ${batchFirst ? bulkStyles.batchFirst : ''}`} data-batch-first={batchFirst || undefined} onDrop={this.onDrop} onDragOver={this.onDrag(true)} onDragEnter={this.onDrag(true)} onDragLeave={this.onDrag(false)} data-testid="drop-zone">
                <FileUploadComponent {...this.props}
                    fileSelected={(ev: FileSelectEvent) => this.onSelect(ev)}
                    fileSelectButtonClicked={() => this.telemetry('EDITOR_PICKER_OPENED', { tool: this.landing.tool })}
                    shouldDisplayBtnsAsPrimary={true}
                    acceptedFileTypes={acceptAttr(this.state.bulk)}
                    uploadBtnText={batchFirst ? 'Choose photos (up to ' + BATCH_LIMIT + ')' : copy.button} showGoogleDrive={false} showDropbox={false}
                    uploadBtnClassName="btn-primary" sourceMenuLabel="More upload sources" sourceMenuRoles={true}>
                </FileUploadComponent>
                <div className={batchFirst ? bulkStyles.lead : undefined} data-testid="drop-lead">{this.state.touch ? 'Up' : 'or drop photos here · up'} to {BATCH_LIMIT} at once{batchFirst ? ', saved together as one ZIP' : ''}</div>
                {/* IMAGEEDITOR-40: first-run hints - paste works (desktop keyboards; a touch screen has no Ctrl+V), and the tools
                    beyond the page's own are there. Flag-invariant like the rest of the empty state. */}
                {!this.state.touch && <div className={styles.fmts} data-testid="paste-hint">…or paste an image ({this.state.mac ? '⌘V' : 'Ctrl+V'})</div>}
                <div className={styles.capStrip} data-testid="capability-strip">{CAPABILITY_LINE}</div>
                <div className={styles.fmts} data-testid="editor-fmts">{EMPTY_FORMATS_LINE}</div>
            </div>
            {this.state.rejected && <div className={styles.errorNote} role="alert" data-testid="root-alert">{this.state.rejected}
                {this.state.rejectedLink && <>{' '}<a href={this.state.rejectedLink.href}>{this.state.rejectedLink.label}</a></>}</div>}
            <SessionFilesComponent acceptedExtensions={this.accepted} pageSlug={this.landing.slug} toolName={`${copy.verb} ${copy.noun}`} pageData={this.props.pageData}
                onPick={(picked: File[]) => this.stage(picked)} />
            <div className="gcontent-container">
                <MotiveComponent data={AdSlots.pageTop} containerWidth="100%" containerHeight="90px" location={MotiveLocationTypes.convertPageTop} adWidth="970px" adHeight="90px" showAds={true} className="width_major" />
            </div>
        </div>;
    }

    /** Same three blocks as ConverterPageBase, inside the same 1000px shell (kept from the v1 fix). */
    private seoContent() {
        return <div className={`col-12 ${styles.empty}`} style={{ paddingTop: 0 }} ref={this.seoRef}>
            {this.props.markdown && <div data-outline="md"><FeatureDetailsComponent markdown={this.props.markdown}></FeatureDetailsComponent></div>}
            <RelatedLinksComponent relatedLinks={this.props.relatedLinks}></RelatedLinksComponent>
            <div data-outline="rating"><RatingDisplayComponent rating={this.props.rating} title={this.title} feature={this.feature} description={this.description}></RatingDisplayComponent></div>
        </div>;
    }
}

/** sessionStorage key: the files a hand-off opened on this tab, re-opened on a reload (IMAGEEDITOR-R2 i-6b). */
export const HANDOFF_KEY = 'xc-editor-handoff';
/** First-run capability strip under the picker (IMAGEEDITOR-40). */
export const CAPABILITY_LINE = 'Resize · Crop · Rotate · Flip';   // the batch size is on the line above (review10)

/** i-6b: a reload re-opened only part of a hand-off's set (recent files keep 6; one may have expired). */
export const partlyReopened = (back: number, open: number) => `${back} of your ${open} photos were opened again after the reload. Add the others again, or use a recent file.`;

/** Shown after a reload / Back when the editor had photos open on this entry (J5-8, J10-8, J13-8). */
export const LOST_ON_RELOAD = 'Your photos and edits were not kept when you left the editor. Choose them again, or use a recent file below.';

/** The batch cap, naming what was left out (J3-12). */
export function capMessage(leftOut: string[], wasFull: boolean): string {
    const names = leftOut.length > 3 ? `${leftOut.slice(0, 3).join(', ')} and ${leftOut.length - 3} more` : leftOut.join(', ');
    if (wasFull) return `The batch already has ${BATCH_LIMIT} photos, the most at once${names ? `, so ${names} ${leftOut.length === 1 ? 'was' : 'were'} not added` : ''}. Remove a photo to add another.`;
    return `Only the first ${BATCH_LIMIT} files were added: ${names || 'the rest'} ${leftOut.length === 1 ? 'was' : 'were'} left out.`;
}
